<template>
  <form
    :id="name"
    class="form"
    :class="{
      'form--disabled': showDisabledStyle && disabled,
      'form--dirty': dirty,
      'form--submit-attempted': submitAttempted
    }"
    novalidate
    @submit="submit"
    @input="markFormAsDirty"
    @change="markFormAsDirty"
  >
    <fieldset
      class="form__fieldset"
      :disabled="disabled"
    >
      <slot
        :validate-form="validate"
        :validate="validate"
      />
    </fieldset>
  </form>
</template>



<script>
  import { useForm } from 'vee-validate';


  export default {
    name: 'ValidatedForm',

    provide() {
      const {
        addValidator,
        name,
        removeValidator,
        reset,
        submit,
        validate
      } = this;

      return {
        validatedForm: {
          addValidator,
          name,
          removeValidator,
          reset,
          submit,
          validate
        }
      };
    },

    props: {
      disabled: {
        type: Boolean,
        default: false
      },
      extraValidations: {
        type: Function,
        default: null
      },
      name: {
        type: String,
        required: true
      },
      showDisabledStyle: {
        type: Boolean,
        default: false
      }
    },

    // VeeValidate 4 requires the Composition API to register a form context.
    // setup() is used exclusively for that purpose — all other component logic
    // remains in the Options API below.
    // Exposes: vv4Validate (triggers validation on all registered fields),
    //          vv4ResetForm (clears all field errors and resets values)
    setup() {
      const { validate, resetForm } = useForm();

      return {
        vv4Validate: validate,
        vv4ResetForm: resetForm
      };
    },

    data() {
      return {
        dirty: false,
        submitAttempted: false,
        validators: []
      };
    },

    methods: {
      addValidator(validator) {
        this.validators.push(validator);
      },

      async customValidations() {
        const promises = this.validators.map(async (validator) => {
          const isValid = await validator.validate();
          return isValid;
        });

        const results = await Promise.all(promises);
        const isValid = !results.includes(false);

        return isValid;
      },

      invalidFormHandler() {
        let firstInvalidInput = this.$el.querySelector('[aria-invalid="true"], .invalid');

        if (!firstInvalidInput) {
          return;
        }

        if (firstInvalidInput.type === 'hidden') {
          firstInvalidInput = firstInvalidInput.parentNode.querySelector('input:not([type="hidden"]), [data-fake-input]');

          if (!firstInvalidInput) {
            return;
          }
        }

        if (firstInvalidInput.getAttribute('tabindex') !== '-1') {
          firstInvalidInput.focus();

          firstInvalidInput.scrollIntoView({
            block: 'center',
            behavior: 'smooth'
          });
        }
      },

      markFormAsDirty(event) {
        if (event.target && event.target.hasAttribute('data-disable-dirty-form')) {
          return;
        }

        this.dirty = true;
        this.$emit('dirty');
      },

      removeValidator(name) {
        this.validators = this.validators.filter(v => v.name !== name);
      },

      resetValidation() {
        this.vv4ResetForm();
      },

      async submit(event) {
        if (event) {
          event.preventDefault();
        }

        const isValid = await this.validate();

        if (isValid) {
          this.$emit('valid-submit');
        }

        else {
          this.$emit('invalid-submit');
        }
      },

      async validate() {
        this.submitAttempted = true;

        const { valid: formIsValid } = await this.vv4Validate();
        const customValidationsPassed = await this.customValidations();
        let extraValidationsPassed = true;

        if (formIsValid && customValidationsPassed && this.extraValidations) {
          extraValidationsPassed = await this.extraValidations();
        }

        const isValid = formIsValid && customValidationsPassed && extraValidationsPassed;

        if (!isValid) {
          this.invalidFormHandler();
        }

        return isValid;
      }
    }
  };
</script>
